import React, { useState } from 'react';
import { tracking, API_URL } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Search, Package, Truck, CheckCircle2, Clock, MapPin, ExternalLink, ArrowRight, ShoppingBag, AlertCircle } from 'lucide-react';
import { SEO } from '../components/SEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_654bd78e-5e39-4822-a480-29d8c14fa990/artifacts/ow0h2qv9_Black%20and%20White%20Minimalist%20Round%20Beauty%20And%20Spa%20Logo.png";

const STATUS_STEPS = [
  { key: 'new_order', label: 'Order Placed', icon: ShoppingBag },
  { key: 'payment_confirmed', label: 'Payment Confirmed', icon: CheckCircle2 },
  { key: 'processing', label: 'Processing', icon: Clock },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: Package },
];

const STATUS_ORDER = ['new_order', 'payment_confirmed', 'processing', 'picking', 'packed', 'ready_to_ship', 'shipped', 'out_for_delivery', 'delivered'];

const getStepIndex = (status) => {
  if (['new_order'].includes(status)) return 0;
  if (['payment_confirmed'].includes(status)) return 1;
  if (['processing', 'picking', 'packed', 'ready_to_ship'].includes(status)) return 2;
  if (['shipped', 'out_for_delivery'].includes(status)) return 3;
  if (['delivered'].includes(status)) return 4;
  return -1;
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

export const TrackOrder = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) {
      toast.error('Please enter both order number and email');
      return;
    }
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const data = await tracking.lookup(orderNumber.trim(), email.trim());
      setOrder(data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Order not found. Please check your details.';
      setError(msg);
      toast.error(msg);
    }
    setLoading(false);
  };

  const isCancelled = order && ['cancelled', 'returned', 'refunded', 'on_hold'].includes(order.status);
  const currentStep = order ? getStepIndex(order.status) : -1;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="track-order-page">
      <SEO title="Track Your Order" description="Enter your order number and email to check the status of your Vintage Wardrobe Co order." path="/track-order" />
      {/* Hero */}
      <div className="bg-[#0A0A0A] text-white py-16 md:py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <img src={LOGO_URL} alt="Vintage Wardrobe Co" className="w-16 h-16 rounded-full object-cover mx-auto mb-6 border-2 border-[#C9A84C]/30" />
          <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight mb-3" data-testid="track-order-title">Track Your Order</h1>
          <p className="text-sm text-white/50 max-w-md mx-auto">Enter your order number and email address to check the status of your order.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-8">
        {/* Search Form */}
        <form onSubmit={handleTrack} className="bg-white rounded-2xl shadow-lg border border-[#E2E2E2] p-6 md:p-8 mb-8" data-testid="track-order-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#666]">Order Number</Label>
              <Input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g. A1B2C3D4"
                className="mt-1 bg-[#F9F8F6] border-[#E2E2E2] text-[#0A0A0A]"
                data-testid="track-order-number-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#666]">Email Address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-1 bg-[#F9F8F6] border-[#E2E2E2] text-[#0A0A0A]"
                data-testid="track-order-email-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A0A0A] text-white hover:bg-[#0A0A0A] h-12 text-sm uppercase tracking-widest font-bold"
            data-testid="track-order-submit-btn"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'Searching...' : 'Track Order'}
          </Button>
          {error && (
            <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="track-order-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}
        </form>

        {/* Order Results */}
        {order && (
          <div className="space-y-6 pb-16" data-testid="track-order-result">
            {/* Order Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-[#E2E2E2] p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-1">Order</p>
                  <h2 className="text-2xl font-bold text-[#0A0A0A]" data-testid="track-order-id">#{order.order_id}</h2>
                  <p className="text-sm text-[#666] mt-1">Placed on {fmtDate(order.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#666] mb-1">Total</p>
                  <p className="text-2xl font-bold text-[#0A0A0A]" data-testid="track-order-total">&pound;{order.total?.toFixed(2)}</p>
                </div>
              </div>

              {/* Status Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider ${
                isCancelled ? 'bg-red-50 text-red-600 border border-red-200' :
                order.status === 'delivered' ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' :
                'bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/20'
              }`} data-testid="track-order-status-badge">
                {order.status === 'delivered' ? <CheckCircle2 className="w-4 h-4" /> :
                 isCancelled ? <AlertCircle className="w-4 h-4" /> :
                 <Truck className="w-4 h-4" />}
                {order.status_label}
              </div>
            </div>

            {/* Status Timeline */}
            {!isCancelled && (
              <div className="bg-white rounded-2xl shadow-sm border border-[#E2E2E2] p-6 md:p-8" data-testid="track-order-timeline">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#666] mb-6">Order Progress</h3>
                <div className="flex items-center justify-between relative">
                  {/* Progress Line */}
                  <div className="absolute top-5 left-0 right-0 h-0.5 bg-[#E2E2E2]" />
                  <div className="absolute top-5 left-0 h-0.5 bg-[#C9A84C] transition-all duration-700" style={{ width: `${Math.max(0, (currentStep / (STATUS_STEPS.length - 1)) * 100)}%` }} />
                  
                  {STATUS_STEPS.map((step, i) => {
                    const Icon = step.icon;
                    const isComplete = i <= currentStep;
                    const isCurrent = i === currentStep;
                    return (
                      <div key={step.key} className="relative flex flex-col items-center z-10" data-testid={`timeline-step-${step.key}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                          isComplete ? 'bg-[#C9A84C] border-[#C9A84C] text-white' :
                          'bg-white border-[#E2E2E2] text-[#666]/30'
                        } ${isCurrent ? 'ring-4 ring-[#C9A84C]/20 scale-110' : ''}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <p className={`text-[10px] font-bold uppercase tracking-wider mt-2 text-center max-w-[80px] ${
                          isComplete ? 'text-[#0A0A0A]' : 'text-[#666]/40'
                        }`}>{step.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tracking Info */}
            {order.tracking && order.tracking.courier && (
              <div className="bg-white rounded-2xl shadow-sm border border-[#E2E2E2] p-6 md:p-8" data-testid="track-order-tracking">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#666] mb-4">Tracking Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] uppercase text-[#666]">Courier</p>
                    <p className="text-sm font-bold text-[#0A0A0A]">{order.tracking.courier}</p>
                  </div>
                  {order.tracking.tracking_number && (
                    <div>
                      <p className="text-[10px] uppercase text-[#666]">Tracking Number</p>
                      <p className="text-sm font-bold text-[#0A0A0A]">{order.tracking.tracking_number}</p>
                    </div>
                  )}
                  {order.tracking.dispatch_date && (
                    <div>
                      <p className="text-[10px] uppercase text-[#666]">Dispatched</p>
                      <p className="text-sm font-bold text-[#0A0A0A]">{fmtDate(order.tracking.dispatch_date)}</p>
                    </div>
                  )}
                  {order.tracking.expected_delivery && (
                    <div>
                      <p className="text-[10px] uppercase text-[#666]">Expected Delivery</p>
                      <p className="text-sm font-bold text-[#0A0A0A]">{fmtDate(order.tracking.expected_delivery)}</p>
                    </div>
                  )}
                </div>
                {order.tracking.tracking_url && (
                  <a href={order.tracking.tracking_url} target="_blank" rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider hover:bg-[#0A0A0A] transition-colors"
                    data-testid="track-parcel-link">
                    <ExternalLink className="w-4 h-4" /> Track with {order.tracking.courier}
                  </a>
                )}
              </div>
            )}

            {/* Product Summary */}
            {order.items && order.items.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-[#E2E2E2] p-6 md:p-8" data-testid="track-order-items">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#666] mb-4">Items in Your Order</h3>
                <div className="space-y-3">
                  {order.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-[#E2E2E2]/50 last:border-0">
                      <div className="w-14 h-14 rounded-lg bg-[#EFE5D6]/30 overflow-hidden flex-shrink-0">
                        <img
                          src={item.image ? `${API_URL}${item.image}` : 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=80'}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0A0A0A] truncate">{item.name}</p>
                        <p className="text-[10px] text-[#666]">Size: {item.size} &middot; Qty: {item.quantity}</p>
                      </div>
                      <p className="text-sm font-bold text-[#0A0A0A] flex-shrink-0">&pound;{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status History */}
            {order.status_history && order.status_history.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-[#E2E2E2] p-6 md:p-8" data-testid="track-order-history">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#666] mb-4">Status Updates</h3>
                <div className="space-y-3">
                  {[...order.status_history].reverse().map((h, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${i === 0 ? 'bg-[#C9A84C]' : 'bg-[#E2E2E2]'}`} />
                      <div>
                        <p className={`text-sm font-medium ${i === 0 ? 'text-[#0A0A0A]' : 'text-[#666]'}`}>{h.status_label}</p>
                        <p className="text-[10px] text-[#666]/60">{fmtTime(h.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="bg-[#0A0A0A] text-white rounded-2xl p-6 md:p-8 text-center">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-2">Need Help?</h3>
              <p className="text-xs text-white/50 mb-4">If you have questions about your order, please contact our support team.</p>
              <a href="mailto:info@ilicconsultancy.com" className="inline-flex items-center gap-2 text-[#C9A84C] hover:text-[#B8733A] text-sm font-bold transition-colors">
                info@ilicconsultancy.com <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
